import {
  apiSlice,
  createClientV2MutationFn,
  createClientV2QueryFn,
} from 'store/slices/api';
import {
  ClientV2PATCHRequest,
  ClientV2BulkActionRequest,
  ClientV2PATCHResponse,
  ClientV2POSTRequest,
  ListResponse,
  QueryParams,
  AsyncJobResponse,
} from 'types/api';
import { CLIENT_V2_URLS } from 'store/slices/constants/clientV2';
import { parseQueryParams } from 'store/slices/utils';
import {
  OrgPartActionSchema,
  OrgPartCreateResponse,
  OrgPartPATCHSchema,
  OrgPartPOSTSchema,
  OrgPartRemoveSpecsSchema,
  OrgPartSchema,
  OrgPartSpecsPOSTSchema,
  OrgPartScrapeRequestSchema,
  OrgPartScrapeResponseSchema,
  PublicClassificationSchema,
  PublicPartAssetSchema,
  PublicPartSchema,
  PartsV2SearchSchema,
  PartsV2AllPartsResponse,
  OrgPartMergeSchema,
} from 'types/clientV2/parts';
import _ from 'lodash';
import { RootState } from 'store/index';
import { PublicManufacturerSchema } from 'types/part';
import { Org } from 'types/org';

export const extendedApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * @warning Using ALL schema without filters will cause performance issues due to computed properties
     * on the model in Flagship. When not using filters, use the schema:
     * ['id', 'mpn', 'cofactrId', 'description', 'manufacturer', 'heroImage']
     *
     * @recommendation This schema is represented by type PublicPartSchemaSearch for optimal performance when not applying filters
     */
    getPublicParts: builder.query<
      ListResponse<PublicPartSchema>,
      QueryParams<PublicPartSchema>
    >({
      query: (params) => ({
        url: CLIENT_V2_URLS.PUBLIC_PARTS(
          parseQueryParams<PublicPartSchema>(params)
        ),
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(
                ({ id }: PublicPartSchema) =>
                  ({
                    type: 'PublicPart',
                    id,
                  } as const)
              ),
              { type: 'PublicPart', id: 'LIST' },
            ]
          : [{ type: 'PublicPart', id: 'LIST' }],
    }),
    /**
     * Search endpoint for public parts.
     *
     * NOTE: This endpoint operates differently from other clientV2 endpoints:
     * - It uses a simplified parameter structure (search, limit, mpn, mfr) instead of the standard QueryParams
     * - Standard query parameters (schema, filters, etc.) are not supported
     * - This is intentional as the search functionality requires a different approach
     *
     * Unlike other ClientV2 endpoints which directly access models (e.g., /client/boms/, /client/bom_lines/),
     * this endpoint does not operate on the PublicPart model directly. Instead, it acts as a proxy to KB,
     * performing search operations across their schema.
     * However, the data is returned in the same format as clientV2 data (ListResponse<PublicPartSchema>)
     *
     * @param search - Optional search term to query for
     * @param limit - Optional limit for the number of results
     * @param mpn - Optional mpn to query for as MPN or Alias MPN
     * @param mfr - Optional mfr to query for as Manufacturer or Alias Manufacturer
     * @param external - Optional external to query External parts in kb
     */
    searchPublicParts: builder.query<
      ListResponse<PublicPartSchema>,
      PartsV2SearchSchema
    >({
      query: ({ search, limit, mpn, mfr, external }) => ({
        url: CLIENT_V2_URLS.PUBLIC_PART_SEARCH({
          search,
          limit,
          mpn,
          mfr,
          external,
        }),
      }),
      providesTags: (_result, _error, { search, limit, mpn, mfr }) => [
        {
          type: 'PublicPartSearch',
          id: `LIST-${search || ''}-${limit || ''}-${mpn || ''}-${mfr || ''}`,
        },
      ],
    }),
    /**
     * Search endpoint for org parts.
     *
     * Behaves differently from other Client V2 endpoints.
     * @see PartsSearchEndpoint for more detail.
     *
     * @param search - Optional search term to query for
     * @param limit - Optional limit for the number of results
     * @param mpn - Optional mpn to query for as MPN or Alias MPN
     * @param mfr - Optional mfr to query for as Manufacturer or Alias Manufacturer
     */
    searchOrgParts: builder.query<
      ListResponse<OrgPartSchema>,
      PartsV2SearchSchema
    >({
      query: ({ search, limit, mpn, mfr }) => ({
        url: CLIENT_V2_URLS.ORG_PART_SEARCH({ search, limit, mpn, mfr }),
      }),
      providesTags: (result, _error, { search, limit, mpn, mfr }) =>
        result
          ? [
              ...result.data.map(
                ({ id }: OrgPartSchema) =>
                  ({
                    type: 'OrgPartV2',
                    id,
                  } as const)
              ),
              {
                type: 'OrgPartSearch',
                id: `LIST-${search || ''}-${limit || ''}-${mpn || ''}-${
                  mfr || ''
                }`,
              },
            ]
          : [
              {
                type: 'OrgPartSearch',
                id: `LIST-${search || ''}-${limit || ''}-${mpn || ''}-${
                  mfr || ''
                }`,
              },
            ],
    }),
    /**
     * Search endpoint for all parts.
     *
     * Behaves differently from other Client V2 endpoints.
     * @see PartsSearchEndpoint for more detail.
     *
     * NOTE: Unlike search_org_parts and search_public_parts, which return a client V2 styled ListResponse type
     * SearchAllParts is an anti-pattern and returns a simple object with publicParts and orgParts
     *
     * @param search - Optional search term to query for
     * @param limit - Optional limit for the number of results
     * @param mpn - Optional mpn to query for as MPN or Alias MPN
     * @param mfr - Optional mfr to query for as Manufacturer or Alias Manufacturer
     * @param external - Optional external to query External parts in kb
     */
    searchAllParts: builder.query<PartsV2AllPartsResponse, PartsV2SearchSchema>(
      {
        query: ({ search, limit, mpn, mfr, external, orgId }) => ({
          url: CLIENT_V2_URLS.ALL_PARTS_SEARCH({
            search,
            limit,
            mpn,
            mfr,
            external,
            orgId,
          }),
        }),
        providesTags: (result, _error, { search, limit, mpn, mfr }) =>
          result
            ? [
                ...result.orgParts.map(
                  ({ id }: OrgPartSchema) =>
                    ({
                      type: 'OrgPartV2',
                      id,
                    } as const)
                ),
                {
                  type: 'AllPartSearch',
                  id: `LIST-${search || ''}-${limit || ''}-${mpn || ''}-${
                    mfr || ''
                  }`,
                },
              ]
            : [
                {
                  type: 'AllPartSearch',
                  id: `LIST-${search || ''}-${limit || ''}-${mpn || ''}-${
                    mfr || ''
                  }`,
                },
              ],
      }
    ),
    getPublicPartAssets: builder.query<
      ListResponse<PublicPartAssetSchema>,
      QueryParams<PublicPartAssetSchema>
    >({
      query: (params) => ({
        url: CLIENT_V2_URLS.PUBLIC_PART_ASSETS(
          parseQueryParams<PublicPartAssetSchema>(params)
        ),
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(
                ({ id }: PublicPartAssetSchema) =>
                  ({
                    type: 'PublicPartAsset',
                    id,
                  } as const)
              ),
              { type: 'PublicPartAsset', id: 'LIST' },
            ]
          : [{ type: 'PublicPartAsset', id: 'LIST' }],
    }),
    getOrgParts: builder.query<
      ListResponse<OrgPartSchema>,
      QueryParams<OrgPartSchema> & { overrideOrgId?: string }
    >({
      queryFn: createClientV2QueryFn<
        ListResponse<OrgPartSchema>,
        QueryParams<OrgPartSchema>
      >((params) =>
        CLIENT_V2_URLS.ORG_PARTS(parseQueryParams<OrgPartSchema>(params))
      ),
      providesTags: (result, error, { overrideOrgId }) => {
        const tags = [];
        if (result?.data) {
          tags.push(
            ...result.data.map(({ id }: OrgPartSchema) => ({
              type: 'OrgPartV2' as const,
              id,
            }))
          );
        }
        // Use organization-specific tag if overrideOrgId is provided
        if (overrideOrgId) {
          tags.push({
            type: 'OrgPartV2' as const,
            id: `LIST-${overrideOrgId}`,
          });
        } else {
          tags.push({ type: 'OrgPartV2' as const, id: 'LIST' });
        }
        return tags;
      },
    }),
    createOrgPart: builder.mutation<
      OrgPartCreateResponse,
      ClientV2POSTRequest<OrgPartPOSTSchema> & { overrideOrgId?: Org['id'] }
    >({
      queryFn: createClientV2MutationFn<
        OrgPartCreateResponse,
        ClientV2POSTRequest<OrgPartPOSTSchema> & { overrideOrgId?: Org['id'] }
      >((payload) => ({
        url: CLIENT_V2_URLS.ORG_PARTS(),
        method: 'POST',
        body: payload,
      })),
      invalidatesTags: (result, error, { overrideOrgId }) => {
        const tags = [];
        const ids = [
          ...(result?.createdIds ?? []),
          ...(result?.existingIds ?? []),
        ];
        if (ids.length > 0) {
          tags.push(
            ...ids.map((id) => ({
              type: 'OrgPartV2' as const,
              id,
            }))
          );
        }
        // Only invalidate LIST tag for the specific org if overrideOrgId is provided
        if (overrideOrgId) {
          tags.push({
            type: 'OrgPartV2' as const,
            id: `LIST-${overrideOrgId}`,
          });
        } else {
          tags.push({ type: 'OrgPartV2' as const, id: 'LIST' });
        }
        return tags;
      },
    }),
    updateOrgPart: builder.mutation<
      ClientV2PATCHResponse<OrgPartSchema>,
      ClientV2PATCHRequest<OrgPartPATCHSchema>
    >({
      query: (data) => ({
        url: CLIENT_V2_URLS.ORG_PARTS(),
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (result) =>
        result
          ? _.map(result.updatedIds, (id) => ({
              type: 'OrgPartV2' as const,
              id,
            }))
          : [],
    }),
    addUpdateCustomOrgPartSpecs: builder.mutation<
      ClientV2PATCHResponse<OrgPartSchema>,
      OrgPartActionSchema<OrgPartSpecsPOSTSchema>
    >({
      query: (data) => ({
        url: CLIENT_V2_URLS.ORG_PARTS_ADD_SPECS,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result) =>
        result
          ? _.map(result.updatedIds, (id) => ({
              type: 'OrgPartV2',
              id,
            }))
          : [],
    }),
    getPublicManufacturers: builder.query<
      ListResponse<PublicManufacturerSchema>,
      QueryParams<PublicManufacturerSchema>
    >({
      query: (params) => ({
        url: CLIENT_V2_URLS.PUBLIC_MANUFACTURERS(
          parseQueryParams<PublicManufacturerSchema>(params)
        ),
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(
                ({ id }: PublicManufacturerSchema) =>
                  ({
                    type: 'PublicManufacturer',
                    id,
                  } as const)
              ),
              { type: 'PublicManufacturer', id: 'LIST' },
            ]
          : [{ type: 'PublicManufacturer', id: 'LIST' }],
    }),
    getPublicClassifications: builder.query<
      ListResponse<PublicClassificationSchema>,
      QueryParams<PublicClassificationSchema>
    >({
      query: (params) => ({
        url: CLIENT_V2_URLS.PUBLIC_CLASSIFICATIONS(
          parseQueryParams<PublicClassificationSchema>(params)
        ),
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(
                ({ id }: PublicClassificationSchema) =>
                  ({
                    type: 'PublicClassification',
                    id,
                  } as const)
              ),
              { type: 'PublicClassification', id: 'LIST' },
            ]
          : [{ type: 'PublicClassification', id: 'LIST' }],
    }),
    removeCustomOrgPartSpecs: builder.mutation<
      ClientV2PATCHResponse<OrgPartSchema>,
      OrgPartActionSchema<OrgPartRemoveSpecsSchema>
    >({
      query: ({ ids, specs }) => ({
        url: CLIENT_V2_URLS.ORG_PARTS_REMOVE_SPECS,
        method: 'POST',
        body: { ids, specs: _.map(specs, (spec) => _.snakeCase(spec)) },
      }),
      invalidatesTags: (result) =>
        result
          ? _.map(result.updatedIds, (id) => ({
              type: 'OrgPartV2',
              id,
            }))
          : [],
    }),
    uploadOrgPartHeroImageSingle: builder.mutation<
      ClientV2PATCHResponse<OrgPartSchema>,
      { payload: FormData; id: OrgPartSchema['id'] }
    >({
      queryFn: async ({ payload, id }, { getState }) => {
        const result = await fetch(
          CLIENT_V2_URLS.ORG_PARTS_SET_HERO_IMAGE(id),
          {
            method: 'POST',
            body: payload,
            headers: {
              Authorization: `Bearer ${
                (getState() as RootState).session.authToken
              }`,
            },
          }
        );
        const data = await result.json();
        return { data };
      },
      invalidatesTags: (result) =>
        result
          ? [
              ..._.map(result.updatedIds, (id) => ({
                type: 'OrgPartV2' as const,
                id,
              })),
              { type: 'OrgPartV2', id: 'LIST' },
            ]
          : [],
    }),
    resyncOrgPart: builder.mutation<
      ClientV2PATCHResponse<OrgPartSchema>,
      ClientV2BulkActionRequest<OrgPartSchema>
    >({
      queryFn: createClientV2MutationFn<
        ClientV2PATCHResponse<OrgPartSchema>,
        ClientV2BulkActionRequest<OrgPartSchema>
      >((data) => ({
        url: CLIENT_V2_URLS.ORG_PARTS_RESYNC,
        method: 'POST',
        body: data,
      })),
      invalidatesTags: (result) =>
        result
          ? _.map(result.updatedIds, (id) => ({
              type: 'OrgPartV2',
              id,
            }))
          : [],
    }),
    scrapeOrgPart: builder.mutation<
      AsyncJobResponse<OrgPartScrapeResponseSchema>,
      OrgPartScrapeRequestSchema
    >({
      queryFn: createClientV2MutationFn<
        AsyncJobResponse<OrgPartScrapeResponseSchema>,
        OrgPartScrapeRequestSchema
      >((request) => ({
        url: CLIENT_V2_URLS.ORG_PARTS_SCRAPE,
        method: 'POST',
        body: request,
      })),
    }),
    mergeOrgPart: builder.mutation<void, OrgPartMergeSchema>({
      queryFn: createClientV2MutationFn<void, OrgPartMergeSchema>(
        ({ targetId, sourceIds }) => ({
          url: CLIENT_V2_URLS.ORG_PART_MERGE(targetId),
          method: 'POST',
          body: { ids: sourceIds },
        })
      ),
      invalidatesTags: (_result, _error, { targetId, sourceIds }) => [
        { type: 'OrgPartV2' as const, id: 'LIST' },
        { type: 'OrgPartV2' as const, id: targetId },
        ...sourceIds.map((id) => ({ type: 'OrgPartV2' as const, id })),
      ],
    }),
  }),
});

export const {
  usePrefetch,
  useGetPublicPartsQuery,
  useSearchPublicPartsQuery,
  useLazySearchPublicPartsQuery,
  useSearchOrgPartsQuery,
  useLazySearchOrgPartsQuery,
  useLazySearchAllPartsQuery,
  useGetPublicPartAssetsQuery,
  useLazyGetPublicPartsQuery,
  useGetOrgPartsQuery,
  useLazyGetOrgPartsQuery,
  useCreateOrgPartMutation,
  useUpdateOrgPartMutation,
  useAddUpdateCustomOrgPartSpecsMutation,
  useRemoveCustomOrgPartSpecsMutation,
  useUploadOrgPartHeroImageSingleMutation,
  useResyncOrgPartMutation,
  useScrapeOrgPartMutation,
  useMergeOrgPartMutation,
  useGetPublicManufacturersQuery,
  useGetPublicClassificationsQuery,
} = extendedApiSlice;
